import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import type { MessageType } from '../types/chat.types'
import { groupMessagesByDate } from '../utils/formatMessage'
import { autoScrollToBottom } from '../utils/autoScroll'
import { MessageItem } from './MessageItem'

interface MessageListProps {
  messages: MessageType[]
  footer?: ReactNode
}

export const MessageList = ({ messages, footer }: MessageListProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const grouped = useMemo(() => groupMessagesByDate(messages), [messages])

  useEffect(() => {
    autoScrollToBottom(containerRef.current)
  }, [messages.length, footer])

  return (
    <div
      ref={containerRef}
      className="flex h-full flex-col overflow-y-auto px-6 py-5"
      aria-live="polite"
      aria-label="Conversation messages"
    >
      {grouped.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
            <svg
              className="h-7 w-7 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
              />
            </svg>
          </div>
          <p className="text-base font-semibold text-slate-600">
            Start the conversation
          </p>
          <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-slate-400">
            Send a message to get things going. Your chat is private and
            secure.
          </p>
        </div>
      )}

      {grouped.length > 0 && (
        <div className="mt-auto flex flex-col gap-5">
          {grouped.map(group => (
            <div key={group.dateLabel} className="flex flex-col gap-3">
              <div className="mx-auto rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {group.dateLabel}
              </div>
              <div className="flex flex-col gap-3">
                {group.items.map(item => (
                  <MessageItem key={item.sid} message={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {footer && <div className="mt-2">{footer}</div>}
    </div>
  )
}
